/**
 * CreateBuildDeck — "One at a time" build view (specs/challenge-flow-redesign — Story 2).
 * Shows one upcoming match at a time with its full market picker, a running possible score
 * (the summed odds, NOT a multiplier), Next/Back nav, and an end state.
 */

import React, { useCallback, useMemo } from 'react';
import { View, Text, ActivityIndicator, ScrollView } from 'react-native';
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable';
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants';
import { useCreatePage } from '../../../src/create/contexts/CreatePageContext';
import { useDeckMatches } from '../../../src/create/hooks/useDeckMatches';
import { useMatchDetails } from '../../../src/create/hooks/useMatchDetails';
import { InlineMarketPicker } from '../CreateGamesStep/InlineMarketPicker';
import { SoccerKit } from '../../ui/SoccerKit';
import { TeamCrest } from '../../competitions/TeamCrest/TeamCrest';
import { RaceCardIdentity } from '../CreateGamesStep/RaceCardIdentity';
import { getSportDefinitionOrDefault, OUTCOME_SHAPES } from '../../../src/sports/sports.constants';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { formatTimeOnly, formatDateShort } from '../../../helpers/formatDateTime.helper';
import { isMatchWinnerSlip } from '../../../src/create/types/create.types';
import { readablePickValue } from '../../../src/create/helpers/pickLabel.helper';
import type { Game, PickSlip } from '../../../src/create/types/create.types';
import { CREATE_FLOW_TEST_IDS } from '../../../src/create/constants/create.constants';
import { useFirstChallengeTarget } from '../../../src/onboarding/hooks/useFirstChallengeTarget';
import { FC_TARGET_KEYS } from '../../../src/onboarding/constants/firstChallengeTour.constants';
import { deckStyles as styles } from './CreateBuildDeck.styles';

const PRED_LABEL: Record<'home' | 'draw' | 'away', string> = { home: 'Home', draw: 'Draw', away: 'Away' };

/** Human label + odds for a slip on its game (match-winner uses game odds; others carry their own). */
function describeSlip(slip: PickSlip, game: Game): { label: string; odds: number } {
  if (isMatchWinnerSlip(slip)) {
    const side = slip.prediction;
    const teamLabel = side === 'home' ? game.homeTeam : side === 'away' ? game.awayTeam : 'Draw';
    const odds = (side === 'home' ? game.odds.home : side === 'away' ? game.odds.away : game.odds.draw) ?? 0;
    return { label: `${PRED_LABEL[side]} · ${teamLabel}`, odds };
  }
  return { label: `${slip.marketName ?? 'Pick'} · ${readablePickValue(slip.marketId, slip.value)}`, odds: slip.odd ?? 0 };
}

/** One deck card. Isolated so useMatchDetails runs for exactly the current match. */
const DeckCard: React.FC<{ game: Game }> = ({ game }) => {
  const { match, loading } = useMatchDetails(game.id);
  const { pickSlips, isOtherPickSelected, handleAddOtherPick, handleRemoveSlip } = useCreatePage();
  // First-challenge tour: spotlight the market/odds picker (inert unless the tour is on that step).
  const { viewRef: oddsRef } = useFirstChallengeTarget(FC_TARGET_KEYS.odds);

  const markets = match?.odds?.markets ?? [];
  // Same identity rules as GameInlineCard — branch on the SHAPE and the identity MODE, never on
  // the sport key. Without this the deck drew two football kits and a "VS" for every sport, so a
  // Grand Prix read as "Netherlands Grand Prix VS Circuit Zandvoort" and an NBA game got kits
  // for teams that do not wear any. The deck is the DEFAULT build view, so this was the first
  // thing a new user saw of a non-football fixture.
  const sportDefinition = getSportDefinitionOrDefault(game.sport);
  const usesKits = sportDefinition.identityMode === 'KIT';
  const isFieldSport = sportDefinition.shape === OUTCOME_SHAPES.FIELD;
  const homeKit = game.homeKit ?? getTeamKitConfig(game.homeTeam);
  const awayKit = game.awayKit ?? getTeamKitConfig(game.awayTeam);
  const dateSource = game.time?.includes('T') ? game.time : game.date || game.time;
  const time = dateSource ? formatTimeOnly(dateSource) : '';
  const dateLabel = dateSource ? formatDateShort(dateSource) : '';

  const currentPicks = useMemo(
    () => pickSlips.filter((s) => s.gameId === game.id),
    [pickSlips, game.id]
  );

  return (
    <View style={styles.card} key={game.id}>
      <View style={styles.metaRow}>
        <Text style={styles.league} numberOfLines={1}>{game.league}</Text>
        {(time || dateLabel) ? <Text style={styles.time}>{`${dateLabel}${dateLabel && time ? ' · ' : ''}${time}`}</Text> : null}
      </View>

      {isFieldSport ? (
        <View style={styles.teamsRow}>
          <RaceCardIdentity game={game} />
        </View>
      ) : (
        <View style={styles.teamsRow}>
          <View style={styles.team}>
            {usesKits
              ? <SoccerKit primary={homeKit.primary} secondary={homeKit.secondary} type={homeKit.type} size={28} />
              : <TeamCrest teamName={game.homeTeam} sport={game.sport} size={28} />}
            <Text style={styles.teamName} numberOfLines={1}>{game.homeTeam}</Text>
          </View>
          <Text style={styles.vs}>VS</Text>
          <View style={styles.team}>
            {usesKits
              ? <SoccerKit primary={awayKit.primary} secondary={awayKit.secondary} type={awayKit.type} size={28} />
              : <TeamCrest teamName={game.awayTeam} sport={game.sport} size={28} />}
            <Text style={styles.teamName} numberOfLines={1}>{game.awayTeam}</Text>
          </View>
        </View>
      )}

      {loading ? (
        <View style={styles.loading}>
          <ActivityIndicator size="small" color="#A78BFA" />
          <Text style={styles.loadingText}>Loading markets…</Text>
        </View>
      ) : (
        <View ref={oddsRef}>
          <InlineMarketPicker
            gameId={game.id}
            homeName={game.homeTeam}
            awayName={game.awayTeam}
            markets={markets}
            isSelected={isOtherPickSelected}
            onPick={handleAddOtherPick}
            markPickedChips
          />
        </View>
      )}

      {currentPicks.length > 0 && (
        // One fixed-height horizontal row (scrolls sideways for multi-pick games) so picking never
        // grows the card past the fold on short phone viewports — the chips are self-explanatory.
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.picksBox}>
          <View style={styles.picksRow}>
            {currentPicks.map((slip, i) => {
              const { label, odds } = describeSlip(slip, game);
              return (
                <View key={`${slip.gameId}-${slip.marketId ?? 'mw'}-${i}`} style={styles.pickChip}>
                  <Text style={styles.pickChipText}>{label}</Text>
                  <Text style={styles.pickChipOdds}>{odds.toFixed(2)}</Text>
                  <SoundPressable
                    tapSoundId={SOUND_IDS.UI_TAP_NAV}
                    onPress={() => handleRemoveSlip(slip)}
                    style={styles.pickChipRemove}
                    testID={`create-deck-remove-${i}`}
                  >
                    <Text style={styles.pickChipRemoveText}>×</Text>
                  </SoundPressable>
                </View>
              );
            })}
          </View>
        </ScrollView>
      )}
    </View>
  );
};

export const CreateBuildDeck: React.FC = () => {
  const { games, deckIdx, setDeckIdx, setBuildView, totalPoints, pickSlips, maxGames } = useCreatePage();
  const { current, count, clampedIdx, isEnd, isEmpty } = useDeckMatches(games, deckIdx, maxGames);

  const goNext = useCallback(() => setDeckIdx((i) => Math.min(i + 1, count)), [setDeckIdx, count]);
  const goPrev = useCallback(() => setDeckIdx((i) => Math.max(0, i - 1)), [setDeckIdx]);
  const restart = useCallback(() => setDeckIdx(0), [setDeckIdx]);
  const toList = useCallback(() => setBuildView('list'), [setBuildView]);

  const pickCount = pickSlips.length;
  // Progress tracks picks made out of the user's allowance (maxGames = 5 free / 15 PRO), NOT deck
  // position — so it moves the instant a pick is added, and shows how many of the cap are used (AC-2.2).
  const filledSegments = Math.min(pickCount, maxGames);
  const picksLabel = `${pickCount} of ${maxGames} picked`;
  const scoreLabel = pickCount > 0 ? totalPoints.toFixed(2) : '—';

  if (isEmpty) {
    return (
      <View style={styles.emptyState} testID={CREATE_FLOW_TEST_IDS.deck}>
        <Text style={styles.emptyEmoji}>📅</Text>
        <Text style={styles.endTitle}>No games to pick right now</Text>
        <Text style={styles.endSub}>Check back later, or browse all games.</Text>
        <SoundPressable tapSoundId={SOUND_IDS.UI_TAP_NAV} onPress={toList} style={styles.secondaryBtn}>
          <Text style={styles.secondaryBtnText}>Browse all games ›</Text>
        </SoundPressable>
      </View>
    );
  }

  if (isEnd || !current) {
    return (
      <View style={styles.emptyState} testID={CREATE_FLOW_TEST_IDS.deckEnd}>
        <Text style={styles.emptyEmoji}>🎯</Text>
        <Text style={styles.endTitle}>That&apos;s the full slate</Text>
        <Text style={styles.endSub}>
          You&apos;ve seen all {count} matches. {pickSlips.length} pick{pickSlips.length === 1 ? '' : 's'} locked in.
        </Text>
        <SoundPressable tapSoundId={SOUND_IDS.UI_TAP_NAV} onPress={toList} style={styles.secondaryBtn}>
          <Text style={styles.secondaryBtnText}>Browse all games ›</Text>
        </SoundPressable>
        <SoundPressable tapSoundId={SOUND_IDS.UI_TAP_NAV} onPress={restart} style={styles.ghostBtn}>
          <Text style={styles.ghostBtnText}>Run through again</Text>
        </SoundPressable>
      </View>
    );
  }

  return (
    <View style={styles.container} testID={CREATE_FLOW_TEST_IDS.deck}>
      <View style={styles.progressRow} testID={CREATE_FLOW_TEST_IDS.deckProgress}>
        {Array.from({ length: maxGames }).map((_, i) => (
          <View
            key={i}
            style={[styles.segment, i < filledSegments && styles.segmentDone]}
          />
        ))}
      </View>

      <View style={styles.idxRow}>
        <Text style={styles.idxLabel}>{picksLabel}</Text>
        <Text style={styles.scoreLabel}>
          Possible score{' '}
          <Text style={styles.scoreValue} testID={CREATE_FLOW_TEST_IDS.buildScore}>{scoreLabel}</Text>
        </Text>
      </View>

      {/* Nav stays pinned below (per user: sticky Next). The chrome around this scroll is trimmed so
          the whole card fits on a short phone viewport; the scroll is the fallback for tall markets. */}
      <ScrollView style={styles.cardScroll} showsVerticalScrollIndicator={false}>
        <DeckCard game={current} />
      </ScrollView>

      <View style={styles.navRow}>
        {clampedIdx > 0 && (
          <SoundPressable
            tapSoundId={SOUND_IDS.UI_TAP_NAV}
            onPress={goPrev}
            style={styles.prevBtn}
            testID={CREATE_FLOW_TEST_IDS.deckPrev}
          >
            <Text style={styles.prevBtnText}>‹ Back</Text>
          </SoundPressable>
        )}
        <SoundPressable
          tapSoundId={SOUND_IDS.UI_TAP_NAV}
          onPress={goNext}
          style={styles.nextBtn}
          testID={CREATE_FLOW_TEST_IDS.deckNext}
        >
          <Text style={styles.nextBtnText}>Next ›</Text>
        </SoundPressable>
      </View>
    </View>
  );
};
